import { FunctionComponent, ReactNode, Ref, forwardRef, PropsWithoutRef, ElementType } from 'react';
import styled, { css } from 'styled-components';

import Grid, { GridContainerProps } from '../Grid';
import { defaultThemeProp } from '../../theme';
import { ForwardProps, NoChildrenProp } from '../../types';
import { StyledProgressRing } from '../Progress';

export interface SummaryItemProps extends NoChildrenProp {
  /** Region for the primary information. */
  primary: NonNullable<ReactNode>;
  /** Region for displaying supporting metadata. */
  secondary?: ReactNode;
  /** Region for an Avatar, Icon, or other supporting visual. */
  visual?: ReactNode;
  /** Region for adding Actions, Buttons, or Links. */
  actions?: ReactNode;
  /* Override the default Grid container props on SummaryItem. */
  container?: GridContainerProps;
  /**
   * Determines how the text within primary and secondary fields should handle overflow.
   * @default "wrap"
   */
  overflowStrategy?: 'wrap' | 'ellipsis';
  /** HTML tag to render the SummaryItem's element with. */
  as?: ElementType;
  /** Ref for the SummaryItem. */
  ref?: Ref<HTMLElement>;
}

export const StyledVisual = styled.div`
  > ${StyledProgressRing}, img {
    display: block;
    object-fit: cover;
    width: 2rem;
    height: 2rem;
  }
`;

StyledVisual.defaultProps = defaultThemeProp;

export const StyledPrimary = styled(Grid)<{
  isString?: boolean;
  overflowStrategy?: SummaryItemProps['overflowStrategy'];
}>(({ theme, isString, overflowStrategy }) => {
  return css`
    ${overflowStrategy === 'ellipsis'
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          word-break: break-word;
        `}

    ${isString &&
    css`
      font-weight: ${theme.base['font-weight']['semi-bold']};
    `}
  `;
});

StyledPrimary.defaultProps = defaultThemeProp;

export const StyledSecondary = styled(Grid)<{
  overflowStrategy?: SummaryItemProps['overflowStrategy'];
}>(({ overflowStrategy }) => {
  return css`
    ${overflowStrategy === 'ellipsis'
      ? css`
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          word-break: break-word;
        `}
  `;
});

export const StyledSummaryItemActions = styled.div`
  white-space: nowrap;
`;

export const StyledSummaryItem = styled.div``;

const getColumns = ({ visual, actions }: Pick<SummaryItemProps, 'visual' | 'actions'>) => {
  return `${visual ? 'auto ' : ''}minmax(0, 1fr)${actions ? ' auto' : ''}`;
};

const getAreas = ({
  secondary,
  visual,
  actions
}: Pick<SummaryItemProps, 'secondary' | 'visual' | 'actions'>) => {
  return `"${visual ? 'visual ' : ''}primary${actions ? ' actions' : ''}"${
    secondary ? `\n"${visual ? 'visual ' : ''}secondary${actions ? ' actions' : ''}"` : ''
  }`;
};

const SummaryItem: FunctionComponent<SummaryItemProps & ForwardProps> = forwardRef(
  (
    {
      visual,
      primary,
      secondary,
      actions,
      container,
      overflowStrategy = 'wrap',
      as,
      ...restProps
    }: PropsWithoutRef<SummaryItemProps>,
    ref: SummaryItemProps['ref']
  ) => {
    return (
      <Grid
        {...restProps}
        ref={ref}
        container={{
          cols: getColumns({ visual, actions }),
          colGap: 2,
          areas: getAreas({ secondary, visual, actions }),
          ...container
        }}
        as={StyledSummaryItem}
        forwardedAs={as}
      >
        {visual && (
          <Grid as={StyledVisual} item={{ area: 'visual', alignSelf: 'center' }}>
            {visual}
          </Grid>
        )}
        <StyledPrimary
          item={{ area: 'primary', alignSelf: secondary ? 'end' : 'center' }}
          isString={typeof primary === 'string'}
          overflowStrategy={overflowStrategy}
        >
          {primary}
        </StyledPrimary>
        {secondary && (
          <StyledSecondary
            item={{ area: 'secondary', alignSelf: 'start' }}
            overflowStrategy={overflowStrategy}
          >
            {secondary}
          </StyledSecondary>
        )}
        {actions && (
          <Grid as={StyledSummaryItemActions} item={{ area: 'actions', alignSelf: 'center' }}>
            {actions}
          </Grid>
        )}
      </Grid>
    );
  }
);

export default SummaryItem;
